import React, { createContext, useContext, useState, useEffect } from "react";
import {
  signInWithPopup,
  signOut as firebaseSignOut,
  onAuthStateChanged
} from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { auth, db, googleProvider } from "../firebase";

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPremium, setIsPremium] = useState(false);

  // Escuchar cambios de autenticación
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);

      if (firebaseUser) {
        try {
          // Obtener o crear perfil de usuario en Firestore
          const userDoc = await getDoc(doc(db, "users", firebaseUser.uid));

          if (userDoc.exists()) {
            const profile = userDoc.data();
            setUserProfile(profile);

            // Check if subscription has expired (backup verification)
            let premium = profile.isPremium || false;
            if (premium && profile.subscriptionEnd) {
              const endDate = new Date(profile.subscriptionEnd);
              if (endDate < new Date()) {
                premium = false;
                console.log('Subscription expired:', profile.subscriptionEnd);
              }
            }
            setIsPremium(premium);
          } else {
            // Crear nuevo perfil
            const newProfile = {
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              displayName: firebaseUser.displayName,
              photoURL: firebaseUser.photoURL,
              isPremium: false,
              subscriptionStatus: null,
              subscriptionEnd: null,
              createdAt: new Date().toISOString()
            };

            await setDoc(doc(db, "users", firebaseUser.uid), newProfile);
            setUserProfile(newProfile);
            setIsPremium(false);
          }
        } catch (error) {
          // Handle offline/network errors gracefully
          console.warn("Could not fetch user profile (offline?):", error.message);
          // Use basic user info from Firebase Auth
          setUserProfile({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            photoURL: firebaseUser.photoURL,
            isPremium: false
          });
          setIsPremium(false);
        }
      } else {
        setUserProfile(null);
        setIsPremium(false);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Iniciar sesión con Google
  const signInWithGoogle = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      return result.user;
    } catch (error) {
      console.error("Error signing in with Google:", error);
      throw error;
    }
  };

  // Cerrar sesión
  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      setUser(null);
      setUserProfile(null);
      setIsPremium(false);
    } catch (error) {
      console.error("Error signing out:", error);
      throw error;
    }
  };

  // Actualizar estado premium
  const updatePremiumStatus = async (isPremiumStatus, subscriptionData = {}) => {
    if (!user) return;

    try {
      await setDoc(doc(db, "users", user.uid), {
        ...userProfile,
        isPremium: isPremiumStatus,
        ...subscriptionData,
        updatedAt: new Date().toISOString()
      }, { merge: true });

      setIsPremium(isPremiumStatus);
      setUserProfile(prev => ({
        ...prev,
        isPremium: isPremiumStatus,
        ...subscriptionData
      }));
    } catch (error) {
      // Handle offline errors gracefully - still update local state
      console.warn("Could not update premium status (offline?):", error.message);
      setIsPremium(isPremiumStatus);
      setUserProfile(prev => ({
        ...prev,
        isPremium: isPremiumStatus,
        ...subscriptionData
      }));
    }
  };

  // Verificar suscripción (llamar a la API)
  const checkSubscription = async () => {
    if (!user) return false;

    try {
      const response = await fetch(`/api/subscription-status?uid=${user.uid}`);
      const data = await response.json();

      if (data.isPremium !== isPremium) {
        await updatePremiumStatus(data.isPremium, {
          subscriptionStatus: data.status,
          subscriptionEnd: data.currentPeriodEnd
        });
      }

      return data.isPremium;
    } catch (error) {
      console.error("Error checking subscription:", error);
      return isPremium;
    }
  };

  const value = {
    user,
    userProfile,
    loading,
    isPremium,
    signInWithGoogle,
    signOut,
    updatePremiumStatus,
    checkSubscription
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
